// Nomenclator „Stiluri de geometrie" — un stil are 2 stări (normală + selectată),
// fiecare cu proprietăți în format Leaflet (color/weight/opacity/fillColor/fillOpacity/radius…).

import { IServerColumnConfig } from '@/components/table/column-config';

export type GeometrieTip = 'arie' | 'linie' | 'punct' | 'limita_uat';
type LineCap = 'butt' | 'round' | 'square';
type LineJoin = 'miter' | 'round' | 'bevel';

export const TIP_LABEL: Record<GeometrieTip, string> = {
	arie: 'Arie proiect',
	linie: 'Linie proiect',
	punct: 'Punct proiect',
	limita_uat: 'Limită UAT'
};

// Maparea între tipul de geometrie din BE (geometryType.id) și tipul intern.
export const GEOM_TYPE_BY_ID: Record<number, GeometrieTip> = {
	1: 'punct',
	2: 'linie',
	3: 'arie',
	4: 'limita_uat'
};

export const GEOM_TYPE_ID: Record<GeometrieTip, number> = {
	punct: 1,
	linie: 2,
	arie: 3,
	limita_uat: 4
};

// Status din BE (status.id) — 1 = Activ.
export const STATUS_ACTIVE_ID = 1;
export const STATUS_INACTIVE_ID = 2;

// Stilul etichetei (contract GeoPuls): `text` null/absent = FĂRĂ etichetă; un obiect =
// eticheta se randează cu aceste proprietăți (cu valori implicite pentru cele omise).
// Eticheta are o singură înfățișare — nu există variantă „selectată"; consumatorii
// citesc exclusiv `default.text` (normalStyle.text).
export interface IStilText {
	font?: number; // px, randat ca `bold ${font}px sans-serif`
	offsetX?: number;
	offsetY?: number;
	fill?: { color?: string };
	stroke?: { color?: string; width?: number };
}

// Valorile unei stări (normală sau selectată), în format Leaflet path options.
export interface IStilState {
	// etichetă (doar pe starea normală; null explicit = etichete dezactivate)
	text?: IStilText | null;
	// contur (stroke)
	stroke?: boolean;
	color?: string;
	weight?: number;
	opacity?: number;
	dashArray?: string;
	lineCap?: LineCap;
	lineJoin?: LineJoin;
	// umplere (fill)
	fill?: boolean;
	fillColor?: string;
	fillOpacity?: number;
	// punct
	radius?: number;
	// vizibilitate pe zoom (purtată doar de starea normală): geometria se randează
	// în aplicația mobilă de la minZoom până la maxZoom (exclusiv)
	minZoom?: number;
	maxZoom?: number;
}

export interface IGeometryStyle {
	id: number | string;
	name: string;
	type: GeometrieTip;
	active: boolean;
	statusName?: string;
	default: IStilState;
	selected: IStilState;
	createdByName?: string;
	createdAt?: string;
}

// Payload create/edit — aliniat la contractul BE (geometryTypeId, statusId, normalStyle, selectedStyle).
export interface IGeometryStylePayload {
	name: string;
	type: GeometrieTip;
	active: boolean;
	default: IStilState;
	selected: IStilState;
}

export interface GeometryStylesReturnType {
	data: IGeometryStyle[];
	loading: boolean;
	/** Config-ul de coloane din BE (api/geometry-styles/table-config); null → coloanele locale. */
	tableConfig: IServerColumnConfig[] | null;
	loadingTableConfig: boolean;
	loadingCreate: boolean;
	loadingUpdate: boolean;
	loadingDelete: boolean;
	getStyles: (page: number, items: number) => Promise<void>;
	getStylesQuery: (query: string, page: number, items: number) => Promise<void>;
	getStyle: (styleId: number | string) => Promise<IGeometryStyle | null>;
	createStyle: (payload: IGeometryStylePayload, onSuccess?: () => void) => Promise<void>;
	editStyle: (
		styleId: number | string,
		payload: IGeometryStylePayload,
		onSuccess?: () => void
	) => Promise<void>;
	removeStyle: (styleId: number | string, onSuccess?: () => void) => Promise<void>;
	page: number;
	items: number;
	setPage: (page: number) => void;
	setItems: (items: number) => void;
	totalPages: number;
	totalItems: number;
}
